import { useField } from 'formik';
import { twMerge } from 'tailwind-merge';
import { colors, gradients } from 'theme';
import {
  Box,
  Radio,
  FormLabel,
  RadioGroup,
  FormControl,
  FormHelperText,
  RadioGroupProps,
  FormControlLabel,
} from '@mui/material';

import { controlError } from 'utils';

import { Icon, RadioCheckedIcon } from '../icons';

export interface RadioFieldProps extends Omit<
  RadioGroupProps,
  'name' | 'onChange'
> {
  name: string;
  label?: string;
  padding?: string;
  required?: boolean;
  fullWidth?: boolean;
  withBorder?: boolean;
  itemClassName?: string;
  radioGroupClassName?: string;
  // Required when mapValue stores an object instead of the option value.
  displayValue?: string | string[];
  mapValue?: (value: string) => unknown;
  options: { label: string; value: string | number }[];
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => void;
}

export const RadioField = ({
  sx,
  name,
  label,
  options,
  padding,
  onChange,
  required,
  mapValue,
  fullWidth,
  withBorder,
  displayValue,
  itemClassName,
  radioGroupClassName,
  ...props
}: RadioFieldProps) => {
  const [field, meta, helpers] = useField(name);

  const errorText = controlError(meta, name, label);
  const hasError = Boolean(errorText);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    helpers.setValue(mapValue ? mapValue(value) : value, true);
    onChange?.(event, value);
  };

  return (
    <FormControl error={hasError} className="w-full">
      {label && (
        <FormLabel className="text-black-primary">
          {label}
          {required && <span className="text-red-primary-primary">*</span>}
        </FormLabel>
      )}
      <RadioGroup
        {...props}
        name={field.name}
        onChange={handleChange}
        className={radioGroupClassName}
        value={displayValue ?? field.value ?? ''}
        sx={{
          ...(fullWidth && { flexWrap: 'nowrap' }),
          ...sx,
        }}
      >
        {options.map((opt) => (
          <Box
            key={opt.value}
            sx={{
              ...(fullWidth && { width: '100%' }),
              ...(withBorder
                ? {
                    padding: '2px',
                    borderRadius: '8px',
                    background:
                      (displayValue ?? field.value) === opt.value
                        ? gradients.accentGradient.primary.primary
                        : colors.gray.primary,
                  }
                : {}),
            }}
          >
            <Box
              sx={
                withBorder
                  ? {
                      borderRadius: '6px',
                      background: colors.white.primary,
                      '&:hover': {
                        background: colors.white.hover,
                      },
                      '&:active': {
                        background: colors.white.active,
                      },
                    }
                  : undefined
              }
            >
              <FormControlLabel
                value={opt.value}
                label={
                  <p className="min-h-6 pt-0.5 align-middle">{opt.label}</p>
                }
                className={twMerge(
                  'items-start gap-2 md:w-auto',
                  itemClassName
                )}
                sx={
                  padding
                    ? {
                        padding: padding,
                      }
                    : undefined
                }
                control={
                  <Radio
                    icon={<Icon.RadioEmpty />}
                    checkedIcon={<RadioCheckedIcon />}
                  />
                }
              />
            </Box>
          </Box>
        ))}
      </RadioGroup>
      {hasError && <FormHelperText>{errorText}</FormHelperText>}
    </FormControl>
  );
};
